import { t } from "@lingui/macro";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";

import { selectChartToken } from "context/SyntheticsStateContext/selectors/chartSelectors";
import { selectTradeboxMarketInfo } from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { useChainId } from "lib/chains";
import { getToken } from "sdk/configs/tokens";

import { TableListSkeleton } from "components/Skeleton/Skeleton";
import Tabs from "components/Tabs/Tabs";

import OrderBook from "./OrderBook";
import Trades from "./Trades";

const tabs = [
  {
    value: "order-book",
    label: t`Order book`,
  },
  {
    value: "trades",
    label: t`Trades`,
  },
];

interface Props {
  selectedToken?: ReturnType<typeof getToken>;
}

const MarketPanel = ({ selectedToken }: Props) => {
  const [selectedTab, setSelectedTab] = useState(tabs[0].value);

  const { chainId } = useChainId();
  const { chartToken } = useSelector(selectChartToken);
  const marketInfo = useSelector(selectTradeboxMarketInfo);
  const chartTokenAddress = chartToken?.address;
  const selectedTokenOption =
    marketInfo?.indexToken || selectedToken || (chartTokenAddress ? getToken(chainId, chartTokenAddress) : undefined);
  const panelKey = marketInfo?.marketTokenAddress ?? selectedTokenOption?.address ?? "market-panel";

  return (
    <div className="MarketPanel flex h-max w-full flex-col gap-12 rounded-[8px] bg-gray-darker px-12 pb-12 pt-8 sm:h-full sm:max-h-full sm:w-[340px] sm:overflow-y-auto">
      <Tabs
        options={tabs}
        selectedValue={selectedTab}
        onChange={setSelectedTab}
        variant="filled"
        size="medium"
        className="MarketPanel-tabs"
      />
      {selectedTokenOption ? (
        <>
          {selectedTab === "order-book" ? (
            <OrderBook key={`order-book:${panelKey}`} selectedToken={selectedTokenOption} />
          ) : (
            <Trades key={`trades:${panelKey}`} selectedToken={selectedTokenOption} />
          )}
        </>
      ) : (
        <div className="flex flex-col">
          <TableListSkeleton count={5} Structure={StructureSkeleton} />
        </div>
      )}
    </div>
  );
};

const StructureSkeleton = () => {
  return (
    <div className="relative grid grid-cols-3">
      <div className="h-32">
        <Skeleton width={80} height={24} inline />
      </div>
      <div className="h-32">
        <Skeleton width={80} height={24} />
      </div>
      <div className="flex h-32 w-full justify-end">
        <Skeleton width={80} height={24} />
      </div>
    </div>
  );
};

export default MarketPanel;
